import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../FirebaseConfig';
import { useCart } from '../component/CartContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ShieldCheck, MessageCircle, Truck, Minus, Plus,
    ShoppingCart, Share2, MapPin, Tag, Star, ArrowLeft, Zap, Phone
} from 'lucide-react';
import noImg from "../assets/ChatGPT Image May 4, 2025, 05_02_10 AM.png";
import * as THREE from 'three';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// ── Black space / starfield background (matches ProductPage theme) ─────────────
const SpaceBg = () => {
    const canvasRef = useRef(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const sizes = { width: window.innerWidth, height: window.innerHeight };
        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(60, sizes.width / sizes.height, 0.1, 2000);
        camera.position.z = 1;

        const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: false });
        renderer.setSize(sizes.width, sizes.height);
        renderer.setPixelRatio(1);

        // Static stars
        const starCount = 600;
        const starPos = new Float32Array(starCount * 3);
        for (let i = 0; i < starCount; i++) {
            starPos[i * 3]     = (Math.random() - 0.5) * 2000;
            starPos[i * 3 + 1] = (Math.random() - 0.5) * 2000;
            starPos[i * 3 + 2] = (Math.random() - 0.5) * 1500 - 200;
        }
        const starGeo = new THREE.BufferGeometry();
        starGeo.setAttribute('position', new THREE.BufferAttribute(starPos, 3));
        const starMat = new THREE.PointsMaterial({ color: 0xffffff, size: 1.0, sizeAttenuation: true, transparent: true, opacity: 0.7 });
        scene.add(new THREE.Points(starGeo, starMat));

        // Drifting dust
        const dustCount = 120;
        const dustPos = new Float32Array(dustCount * 3);
        const dustVel = new Float32Array(dustCount * 3);
        for (let i = 0; i < dustCount; i++) {
            dustPos[i * 3]     = (Math.random() - 0.5) * 1800;
            dustPos[i * 3 + 1] = (Math.random() - 0.5) * 1800;
            dustPos[i * 3 + 2] = (Math.random() - 0.5) * 800;
            dustVel[i * 3]     = (Math.random() - 0.5) * 0.012;
            dustVel[i * 3 + 1] = (Math.random() - 0.5) * 0.012;
        }
        const dustGeo = new THREE.BufferGeometry();
        dustGeo.setAttribute('position', new THREE.BufferAttribute(dustPos, 3));
        const dustMat = new THREE.PointsMaterial({ color: 0xaaaaaa, size: 0.5, sizeAttenuation: true, transparent: true, opacity: 0.3 });
        const dust = new THREE.Points(dustGeo, dustMat);
        scene.add(dust);

        let raf, frame = 0;
        const animate = () => {
            frame++;
            for (let i = 0; i < dustCount; i++) {
                dustPos[i * 3]     += dustVel[i * 3];
                dustPos[i * 3 + 1] += dustVel[i * 3 + 1];
                if (Math.abs(dustPos[i * 3])     > 900) dustPos[i * 3]     = (Math.random() - 0.5) * 600;
                if (Math.abs(dustPos[i * 3 + 1]) > 900) dustPos[i * 3 + 1] = (Math.random() - 0.5) * 600;
            }
            dustGeo.attributes.position.needsUpdate = true;
            camera.position.x = Math.sin(frame * 0.0003) * 0.4;
            camera.position.y = Math.cos(frame * 0.0002) * 0.4;
            if (frame % 90 === 0) starMat.opacity = 0.6 + Math.random() * 0.25;
            renderer.render(scene, camera);
            raf = requestAnimationFrame(animate);
        };

        const onResize = () => {
            sizes.width = window.innerWidth; sizes.height = window.innerHeight;
            camera.aspect = sizes.width / sizes.height;
            camera.updateProjectionMatrix();
            renderer.setSize(sizes.width, sizes.height);
        };
        window.addEventListener('resize', onResize);
        animate();
        return () => { window.removeEventListener('resize', onResize); cancelAnimationFrame(raf); renderer.dispose(); };
    }, []);
    return <canvas ref={canvasRef} className="fixed inset-0 w-full h-full z-0 pointer-events-none opacity-50" />;
};

// ── Skeleton loader ────────────────────────────────────────────────────────────
const Skeleton = ({ className }) => (
    <div className={`animate-pulse bg-white/10 rounded-xl ${className}`} />
);

// ── Related product mini-card ──────────────────────────────────────────────────
const RelatedCard = ({ product }) => (
    <Link
        to={`/product/${product.id}`}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="group flex flex-col bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-green-500/40 transition-all duration-300"
    >
        <div className="w-full h-36 overflow-hidden">
            <img
                src={product.imageUrl || noImg}
                alt={product.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
        </div>
        <div className="p-3">
            <p className="text-white text-xs font-semibold line-clamp-2 mb-1">{product.name}</p>
            <p className="text-green-400 font-bold text-sm">₦{Number(product.price).toLocaleString()}</p>
        </div>
    </Link>
);

// ── Badge component ────────────────────────────────────────────────────────────
const Badge = ({ children, color = 'green' }) => {
    const colors = {
        green:  'bg-green-500/15  text-green-400  border-green-500/30',
        blue:   'bg-blue-500/15   text-blue-400   border-blue-500/30',
        yellow: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
        red:    'bg-red-500/15    text-red-400    border-red-500/30',
    };
    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${colors[color]}`}>
            {children}
        </span>
    );
};

// ── Main component ─────────────────────────────────────────────────────────────
const Addtocart = (props) => {
    const { id } = useParams();
    const [product, setProduct]                   = useState(null);
    const [quantity, setQuantity]                 = useState(1);
    const [loading, setLoading]                   = useState(true);
    const [relatedProducts, setRelatedProducts]   = useState([]);
    const [showHowItWorks, setShowHowItWorks]     = useState(false);
    const [showSupportModal, setShowSupportModal] = useState(false);
    const [imgLoaded, setImgLoaded]               = useState(false);
    const [addedPulse, setAddedPulse]             = useState(false);
    const [buyPulse, setBuyPulse]                 = useState(false);

    const { addToCart } = useCart();
    const navigate = useNavigate();

    // ── Fetch product ──────────────────────────────────────────────────────────
    useEffect(() => {
        setLoading(true);
        setImgLoaded(false);
        getDoc(doc(db, 'Products', id))
            .then(snap => setProduct(snap.exists() ? { id: snap.id, ...snap.data() } : null))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [id]);

    // ── Fetch related ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (!product?.category) return;
        getDocs(query(
            collection(db, 'Products'),
            where('category', '==', product.category),
            where('approved', '==', true),
            limit(6)
        )).then(snap => {
            setRelatedProducts(snap.docs.filter(d => d.id !== id).map(d => ({ id: d.id, ...d.data() })));
        }).catch(console.error);
    }, [product, id]);

    // ── Meta tags ──────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!product) return;
        document.title = `${product.name} — uCart`;
        return () => { document.title = 'uCart'; };
    }, [product]);

    // ── Save to recently viewed ────────────────────────────────────────────────
    useEffect(() => {
        if (!id) return;
        const prev = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
        localStorage.setItem('recentlyViewed', JSON.stringify(
            [id, ...prev.filter(i => i !== id)].slice(0, 10)
        ));
    }, [id]);

    const handleAddToCart = () => {
        if (!props.isSigned) {
            toast.error('Sign in to add items to cart.', { theme: 'dark' });
            navigate('/Login');
            return;
        }
        addToCart({ ...product, quantity });
        setAddedPulse(true);
        setTimeout(() => setAddedPulse(false), 600);
        toast.success(`${quantity} × ${product.name} added!`, { theme: 'dark', position: 'bottom-right' });
    };

    const handleBuyNow = () => {
        if (!props.isSigned) {
            toast.error('Sign in to continue.', { theme: 'dark' });
            navigate('/Login');
            return;
        }
        addToCart({ ...product, quantity });
        setBuyPulse(true);
        setTimeout(() => { setBuyPulse(false); navigate('/checkout'); }, 300);
    };

    const handleShare = async () => {
        const url = window.location.href;
        try {
            if (navigator.canShare?.({ url })) await navigator.share({ title: product.name, url });
            else { await navigator.clipboard.writeText(url); toast.success('Link copied!', { theme: 'dark' }); }
        } catch {
            await navigator.clipboard.writeText(url);
            toast.success('Link copied!', { theme: 'dark' });
        }
    };

    // ── Loading skeleton ───────────────────────────────────────────────────────
    if (loading) return (
        <div className="min-h-screen bg-slate-950 font-['Sora',sans-serif] text-white p-6 pt-24">
            <SpaceBg />
            <div className="max-w-6xl mx-auto relative z-10">
                <Skeleton className="w-24 h-8 mb-8" />
                <div className="flex flex-col lg:flex-row gap-12">
                    <Skeleton className="lg:w-1/2 aspect-square rounded-3xl" />
                    <div className="lg:w-1/2 space-y-5">
                        <Skeleton className="h-6 w-24" />
                        <Skeleton className="h-12 w-3/4" />
                        <Skeleton className="h-10 w-1/3" />
                        <Skeleton className="h-24 w-full" />
                        <Skeleton className="h-14 w-full" />
                    </div>
                </div>
            </div>
        </div>
    );

    if (!product) return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white">
            <div className="text-center">
                <p className="text-6xl mb-4">🔍</p>
                <h2 className="text-2xl font-bold mb-2">Product not found</h2>
                <Link to="/products" className="text-green-400 underline">Browse all products</Link>
            </div>
        </div>
    );

    const isNew = product.createdAt?.seconds
        ? (Date.now() - product.createdAt.seconds * 1000) < 3 * 24 * 60 * 60 * 1000
        : false;

    return (
        <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden">
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&display=swap');`}</style>
            <SpaceBg />
            <ToastContainer theme="dark" position="bottom-right" />

            <div className="relative z-10 px-4 sm:px-6 lg:px-8 pt-20 pb-24 max-w-6xl mx-auto">

                {/* Back button */}
                <motion.button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-gray-400 hover:text-green-400 transition-colors mb-8 group"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                    <span className="text-sm">Back</span>
                </motion.button>

                {/* Main product card */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                    className="bg-white/[0.03] backdrop-blur-2xl border border-white/10 rounded-3xl overflow-hidden"
                >
                    <div className="flex flex-col lg:flex-row">

                        {/* ── LEFT: Image ──────────────────────────────────────────── */}
                        <div className="lg:w-[46%] relative bg-white/[0.02] border-b lg:border-b-0 lg:border-r border-white/10">
                            {/* Badges */}
                            <div className="absolute top-4 left-4 z-20 flex flex-col gap-2">
                                {isNew && <Badge color="blue"><Zap size={10} /> Just Listed</Badge>}
                                {product.condition && (
                                    <Badge color={product.condition === 'New' ? 'green' : 'yellow'}>
                                        {product.condition === 'New' ? '✦ Brand New' : '♻ Used'}
                                    </Badge>
                                )}
                            </div>

                            {/* Image */}
                            <div className="relative w-full aspect-square flex items-center justify-center p-8 overflow-hidden">
                                {!imgLoaded && <Skeleton className="absolute inset-4 rounded-2xl" />}
                                <motion.img
                                    src={product.imageUrl || noImg}
                                    alt={product.name}
                                    onLoad={() => setImgLoaded(true)}
                                    className={`w-full h-full object-contain rounded-2xl transition-all duration-700 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
                                    whileHover={{ scale: 1.03 }}
                                    transition={{ duration: 0.4 }}
                                />
                            </div>

                            {/* Bottom info strip */}
                            <div className="px-6 pb-6 flex flex-col gap-2">
                                <div className="flex items-start gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 w-full">
                                    <MapPin size={13} className="text-green-400 shrink-0 mt-0.5" />
                                    <span className="text-xs text-gray-300 leading-relaxed">{product.DeliveryAddress || 'Pickup on campus'}</span>
                                </div>
                                {/* FIX: use product.sellerId instead of the undefined seller.id */}
                                {product.storeName && (
                                    <Link
                                        to={`/store/${product.sellerId}`}
                                        className="flex items-start gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 w-full hover:border-green-500/30 transition-colors"
                                    >
                                        <Tag size={13} className="text-green-400 shrink-0 mt-0.5" />
                                        <span className="text-xs text-gray-300 leading-relaxed">{product.storeName}</span>
                                    </Link>
                                )}
                            </div>
                        </div>

                        {/* ── RIGHT: Details ────────────────────────────────────────── */}
                        <div className="lg:w-[54%] flex flex-col p-6 sm:p-10">

                            {/* Category */}
                            <p className="text-xs font-semibold tracking-[0.15em] text-green-500 uppercase mb-3">
                                {product.category}
                            </p>

                            {/* Title */}
                            <h1 className="text-2xl sm:text-4xl font-bold text-white leading-tight mb-4">
                                {product.name}
                            </h1>

                            {/* Price */}
                            <div className="flex items-baseline gap-3 mb-2">
                                <span className="text-4xl sm:text-5xl font-extrabold text-green-400 tracking-tight">
                                    ₦{Number(product.price).toLocaleString()}
                                </span>
                            </div>

                            {/* Buyer protection */}
                            <div className="flex items-center gap-2 text-xs text-green-400/70 mb-6">
                                <ShieldCheck size={13} />
                                <span>Buyer Protection — safe until you receive it</span>
                            </div>

                            {/* Description */}
                            <p className="text-gray-300 text-sm leading-relaxed mb-6 bg-white/[0.03] border border-white/10 rounded-2xl p-4">
                                {product.description}
                            </p>

                            {/* Meta chips */}
                            <div className="flex flex-wrap gap-2 mb-6">
                                {product.condition && (
                                    <span className={`text-xs px-3 py-1 rounded-full border font-medium ${
                                        product.condition === 'New'
                                            ? 'bg-green-500/10 border-green-500/30 text-green-400'
                                            : 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
                                    }`}>
                                        {product.condition === 'Used' && product.defects
                                            ? `Used · ${product.defects}`
                                            : product.condition}
                                    </span>
                                )}
                                {product.university && (
                                    <span className="text-xs px-3 py-1.5 rounded-2xl border bg-blue-500/10 border-blue-500/30 text-blue-400 leading-relaxed">
                                        🎓 {product.university}
                                    </span>
                                )}
                                {product.DeliveryFee > 0 && (
                                    <span className="text-xs px-3 py-1 rounded-full border bg-white/5 border-white/15 text-gray-400">
                                        <Truck size={10} className="inline mr-1" />
                                        Delivery fee ₦{Number(product.DeliveryFee).toLocaleString()}
                                    </span>
                                )}
                            </div>

                            {/* Trust row */}
                            <div className="grid grid-cols-2 gap-3 mb-6">
                                <div className="flex items-center gap-3 bg-green-500/5 border border-green-500/20 rounded-2xl p-3">
                                    <div className="w-8 h-8 rounded-full bg-green-500/15 flex items-center justify-center shrink-0">
                                        <ShieldCheck size={15} className="text-green-400" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-semibold text-white">Verified Seller</p>
                                        <p className="text-[10px] text-gray-500">ID checked</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 bg-green-500/5 border border-green-500/20 rounded-2xl p-3">
                                    <div className="w-8 h-8 rounded-full bg-green-500/15 flex items-center justify-center shrink-0">
                                        <Truck size={15} className="text-green-400" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-semibold text-white">Campus Pickup</p>
                                        <p className="text-[10px] text-gray-500 leading-relaxed">{product.DeliveryAddress || 'On campus'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* How it works accordion */}
                            <div className="mb-6 border border-white/10 rounded-2xl overflow-hidden">
                                <button
                                    onClick={() => setShowHowItWorks(v => !v)}
                                    className="flex items-center justify-between w-full px-4 py-3 text-sm font-semibold text-green-400 hover:bg-white/5 transition-colors"
                                >
                                    <span className="flex items-center gap-2"><Star size={14} /> How buying works</span>
                                    <motion.span
                                        animate={{ rotate: showHowItWorks ? 45 : 0 }}
                                        transition={{ duration: 0.2 }}
                                        className="text-xl leading-none"
                                    >+</motion.span>
                                </button>
                                <AnimatePresence>
                                    {showHowItWorks && (
                                        <motion.div
                                            initial={{ height: 0 }}
                                            animate={{ height: 'auto' }}
                                            exit={{ height: 0 }}
                                            transition={{ duration: 0.3 }}
                                            className="overflow-hidden"
                                        >
                                            <div className="px-4 pb-4 space-y-2 border-t border-white/10 pt-3">
                                                {[
                                                    'Place your order & pay securely',
                                                    'Seller gets notified instantly',
                                                    'Seller confirms availability',
                                                    'uCart calls to confirm pickup',
                                                    'Pick up at the designated station',
                                                    'Confirm receipt — payout released',
                                                ].map((step, i) => (
                                                    <div key={i} className="flex items-center gap-3 text-xs text-gray-300">
                                                        <span className="w-5 h-5 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center font-bold shrink-0 text-[10px]">
                                                            {i + 1}
                                                        </span>
                                                        {step}
                                                    </div>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            {/* Quantity selector */}
                            <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-2xl px-4 py-3 mb-4">
                                <span className="text-sm font-medium text-gray-300">Quantity</span>
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => setQuantity(q => Math.max(1, q - 1))}
                                        disabled={quantity <= 1}
                                        className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 flex items-center justify-center transition-colors"
                                    >
                                        <Minus size={14} />
                                    </button>
                                    <span className="w-8 text-center font-bold text-lg">{quantity}</span>
                                    <button
                                        onClick={() => setQuantity(q => q + 1)}
                                        className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                                    >
                                        <Plus size={14} />
                                    </button>
                                </div>
                            </div>

                            {/* CTA buttons — Row 1 */}
                            <div className="flex flex-col sm:flex-row gap-3 mb-3">
                                <motion.button
                                    onClick={handleAddToCart}
                                    animate={addedPulse ? { scale: [1, 1.05, 1] } : {}}
                                    transition={{ duration: 0.3 }}
                                    className="flex-1 flex items-center justify-center gap-2 bg-white/8 hover:bg-white/15 border border-white/15 hover:border-green-500/40 text-white font-semibold py-4 rounded-2xl transition-all"
                                    whileTap={{ scale: 0.97 }}
                                >
                                    <ShoppingCart size={17} />
                                    Add to Cart
                                </motion.button>

                                <motion.button
                                    onClick={handleBuyNow}
                                    animate={buyPulse ? { scale: [1, 1.05, 1] } : {}}
                                    transition={{ duration: 0.3 }}
                                    className="flex-1 flex items-center justify-center gap-2 bg-green-500 hover:bg-green-400 text-black font-bold py-4 rounded-2xl transition-all shadow-lg shadow-green-500/25"
                                    whileTap={{ scale: 0.97 }}
                                >
                                    <Zap size={17} />
                                    Buy Now · ₦{(Number(product.price) * quantity).toLocaleString()}
                                </motion.button>
                            </div>

                            {/* CTA buttons — Row 2 */}
                            <div className="flex gap-3">
                                <motion.a
                                    href="tel:08142397718"
                                    className="flex-1 flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 hover:border-green-500/30 text-green-400 font-semibold py-3 rounded-2xl transition-all text-sm"
                                    whileTap={{ scale: 0.97 }}
                                    title="Call to order"
                                >
                                    <Phone size={15} />
                                    Call to Order
                                </motion.a>

                                <motion.button
                                    onClick={handleShare}
                                    className="w-12 flex items-center justify-center border border-white/15 hover:border-green-500/40 rounded-2xl py-3 px-3 transition-colors"
                                    whileTap={{ scale: 0.97 }}
                                    title="Share"
                                >
                                    <Share2 size={17} className="text-gray-400" />
                                </motion.button>

                                <motion.button
                                    onClick={() => setShowSupportModal(true)}
                                    className="w-12 flex items-center justify-center border border-white/15 hover:border-blue-500/40 rounded-2xl py-3 px-3 transition-colors"
                                    whileTap={{ scale: 0.97 }}
                                    title="Ask uCart"
                                >
                                    <MessageCircle size={17} className="text-blue-400" />
                                </motion.button>
                            </div>

                            <p className="text-[10px] text-gray-600 text-center mt-2 tracking-wide">
                                📞 Call us on <span className="text-gray-500">0814 239 7718</span> to place your order by phone
                            </p>
                        </div>
                    </div>
                </motion.div>

                {/* Related products */}
                {relatedProducts.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.5 }}
                        className="mt-16"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <h2 className="text-xl font-bold text-white">More in {product.category}</h2>
                            <div className="h-px flex-1 bg-white/10" />
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {relatedProducts.slice(0, 5).map((item, i) => (
                                <motion.div
                                    key={item.id}
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 * i, duration: 0.4 }}
                                >
                                    <RelatedCard product={item} />
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </div>

            {/* Support modal */}
            <AnimatePresence>
                {showSupportModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setShowSupportModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.92, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.92, opacity: 0 }}
                            transition={{ duration: 0.25 }}
                            className="bg-[#0f0f0f] border border-white/10 rounded-3xl p-8 max-w-sm w-full text-center"
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="w-14 h-14 rounded-2xl bg-green-500/10 border border-green-500/20 flex items-center justify-center mx-auto mb-5">
                                <MessageCircle size={24} className="text-green-400" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">Got a question?</h3>
                            <p className="text-sm text-gray-400 mb-5 leading-relaxed">
                                Chat with the uCart support team on WhatsApp, or give us a call.
                            </p>
                            <a
                                href={`https://wa.me/2349017177993?text=${encodeURIComponent(`Hi uCart! Question about: *${product?.name}*\n${window.location.href}`)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-3 w-full bg-[#25D366] hover:bg-[#1ebe5d] py-4 rounded-2xl text-white font-bold mb-3 transition-colors"
                            >
                                <MessageCircle size={20} />
                                Chat on WhatsApp
                            </a>
                            <a
                                href="tel:08142397718"
                                className="flex items-center justify-center gap-3 w-full bg-white/5 hover:bg-white/10 border border-white/15 py-4 rounded-2xl text-green-400 font-bold mb-4 transition-colors"
                            >
                                <Phone size={18} />
                                Call 0814 239 7718
                            </a>
                            <button
                                onClick={() => setShowSupportModal(false)}
                                className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
                            >
                                Close
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Addtocart;